var _jsxFileName = "/codebuild/output/src2357418120/src/bitbucket.org/newyuinc/adc-polaris-web/src/components/auth/Login.tsx";
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, InformationalScreen, LoadingModal, Stack, Text } from '@adc/parallax-component-library';
import { APP_RETURN_TIMEOUT, ApiLoginWarning, ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useApiError, useAuth } from 'Hooks';
import { Banner, Footer, FormContainer, Input, InputPassword, MainContainer, TopHeaderBackArrow } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { _config } from 'Utilities/envConfig';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
import { listenForBypassPhone } from 'Services/appBridge';
import { signIn } from 'Services/auth';
const initialValues = {
  email: '',
  password: ''
};
const mapStateToProps = ({
  env: {
    deviceId,
    installationId,
    osType,
    osVersion,
    app,
    appVersion,
    appHash
  },
  nav: {
    redirection
  }
}) => {
  return {
    deviceId,
    installationId,
    osType,
    osVersion,
    app,
    appVersion,
    redirection,
    appHash
  };
};
const connector = connect(mapStateToProps);
const Login = ({
  deviceId,
  installationId,
  osType,
  osVersion,
  app,
  appVersion,
  appHash
}) => {
  const [loading, setLoading] = useState(false);
  const {
    showApiErrorModal,
    showNetworkErrorModal
  } = useApiError();
  const {
    handleLoginError,
    handleLockoutError,
    handleWrongRegionError,
    showHcpError,
    handleAndroidIntegrityError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
    showSessionManagementError,
    showCountryRegionError,
    showCountryNotSupportedError,
    showDefaultLoginError,
    showAppUpgradeError
  } = useAuth('login');
  useEffect(() => {
    if (_config.environment !== 'production') {
      const cleanup = listenForBypassPhone();
      return cleanup;
    }
  }, []);
  const validationSchema = Yup.object().shape({
    email: Yup.string().email(i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail')).required(i18n.t('Login.content.signInForm.formField.email.errors.required')),
    password: Yup.string().required(i18n.t('Login.content.signInForm.formField.password.errors.required'))
  });
  const handleApiRequestError = useCallback((error, resetForm, signInRequest) => {
    var _error$response, _error$response2;
    const data = (_error$response = error.response) !== null && _error$response !== void 0 && _error$response.data ? error.response.data : {
      details: error.code === ErrorCode.NETWORK_ERROR ? ErrorCode.NETWORK_ERROR : i18n.t('Global.modals.errorCommunicatingServer.body')
    };
    notifyApp('adc-webview:signin', {
      status: error.status,
      data
    });
    switch (error === null || error === void 0 || (_error$response2 = error.response) === null || _error$response2 === void 0 ? void 0 : _error$response2.status) {
      case ApiStatus.UNAUTHORIZED:
        {
          var _error$response3;
          const code = (_error$response3 = error.response) === null || _error$response3 === void 0 ? void 0 : _error$response3.data.code;
          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
            return;
          }
          if (code === ApiStatus.ANDROID_INTEGRITY_ERROR) {
            handleAndroidIntegrityError(error);
            return;
          }
          if (code === ApiStatus.SESSION_MANAGEMENT_ERROR) {
            showSessionManagementError(signInRequest);
            return;
          }
          handleLoginError(error, resetForm);
          break;
        }
      case ApiStatus.TOO_MANY_REQUESTS:
        {
          handleLockoutError(error);
          resetForm();
          break;
        }
      case ApiStatus.BAD_REQUEST:
        {
          var _error$response4;
          const code = (_error$response4 = error.response) === null || _error$response4 === void 0 ? void 0 : _error$response4.data.code;

          // Separate the error handling for LLU specifically, remove this if statement is Libre will follow the same error handling
          if (app === 'llu') {
            if (code === ApiStatus.WRONG_REGION) {
              handleWrongRegionError(error, showApiErrorModal, signInRequest);
              return;
            }
            if (code === ApiStatus.UNSUPPORTED_COUNTRY) {
              showCountryNotSupportedError();
              return;
            }
          }

          // Remove this if the above is updated for Libre in addition to llu
          if (code === ApiStatus.UNSUPPORTED_COUNTRY || code === ApiStatus.WRONG_REGION) {
            showCountryRegionError();
            return;
          }
          if (code === ApiStatus.ANDROID_INTEGRITY_ERROR) {
            handleAndroidIntegrityError(error);
            return;
          }
          showDefaultLoginError();
          break;
        }
      case ApiStatus.UPGRADE_REQUIRED:
        {
          // Remove this if statement when Libre follows the same error handling
          if (app === 'llu') {
            showAppUpgradeError();
          }
          break;
        }
      default:
        error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
    }
  }, [showAppUpgradeError, showNetworkErrorModal, showApiErrorModal, handleLoginError, showHcpError, handleAndroidIntegrityError, showSessionManagementError, handleLockoutError, app, showDefaultLoginError, handleWrongRegionError, showCountryNotSupportedError, showCountryRegionError]);
  const navigateToEmailVerification = useCallback((email, password) => {
    mediator.publish('router:navigate', {
      url: '/check-email',
      data: {
        password,
        email
      }
    });
  }, []);
  const navigateToPhoneVerification = useCallback((task, onSuccessfulVerify) => {
    mediator.publish('router:navigate', {
      url: '/verify-number',
      data: {
        onSuccessfulVerify,
        taskID: task.id,
        mobileNumber: task.verificationTarget
      }
    });
  }, []);
  const hasPendingLibreConsents = useCallback(patient => {
    var _consents$hipaa, _consents$crm, _consents$realWorldEv;
    const {
      country,
      consents
    } = patient;
    const isUS = country === 'US';
    const hasOutdatedHealthConsent = isUS ? Object.hasOwn(consents, 'hipaa') && !((_consents$hipaa = consents.hipaa) !== null && _consents$hipaa !== void 0 && _consents$hipaa.at) : Object.hasOwn(consents, 'crm') && !((_consents$crm = consents.crm) !== null && _consents$crm !== void 0 && _consents$crm.at);
    if (hasOutdatedHealthConsent) {
      mediator.publish('router:navigate', '/hipaa');
      return true;
    }
    if (!((_consents$realWorldEv = consents.realWorldEvidence) !== null && _consents$realWorldEv !== void 0 && _consents$realWorldEv.at)) {
      mediator.publish('router:navigate', '/rwe');
      return true;
    }
    return false;
  }, []);
  const handleSignInSuccess = authSession => {
    notifyApp('adc-webview:signin', authSession);
    setTimeout(() => setLoading(false), APP_RETURN_TIMEOUT);
  };
  const signInRequest = useCallback(async (formValues, resetForm, force) => {
    try {
      setLoading(true);
      const {
        email,
        password
      } = formValues;
      const trimmedPassword = password.trim();
      const authSession = await signIn({
        email,
        password: trimmedPassword,
        osType,
        osVersion,
        app,
        appVersion,
        deviceId,
        installationId,
        appHash,
        force
      });
      store.dispatch({
        type: AUTH_SESSION,
        authSession
      });
      if (app === 'llu') {
        var _authSession$authenti;
        const requiresEmailVerification = (_authSession$authenti = authSession.authentication_steps) === null || _authSession$authenti === void 0 ? void 0 : _authSession$authenti.includes(ApiLoginWarning.EMAIL_VERIFICATION);
        if (requiresEmailVerification) {
          navigateToEmailVerification(email, trimmedPassword);
          return;
        }
        handleSignInSuccess(authSession);
      } else {
        var _authSession$verifica;
        const unverifiedPhoneTask = (_authSession$verifica = authSession.verificationTasks) === null || _authSession$verifica === void 0 ? void 0 : _authSession$verifica.find(task => task.verified === false && task.action === ApiLoginWarning.REGISTER_ACTION && task.verificationMethod === ApiLoginWarning.PHONE_VERIFICATION);
        if (unverifiedPhoneTask) {
          navigateToPhoneVerification(unverifiedPhoneTask, async () => {
            const updatedAuthSession = await signIn({
              email,
              password: trimmedPassword,
              osType,
              osVersion,
              app,
              appVersion,
              deviceId,
              installationId,
              appHash,
              force
            });
            store.dispatch({
              type: AUTH_SESSION,
              authSession: updatedAuthSession
            });
            if (!hasPendingLibreConsents(authSession.include.patient)) {
              handleSignInSuccess(updatedAuthSession);
            }
          });
          return;
        }
        if (!hasPendingLibreConsents(authSession.include.patient)) {
          handleSignInSuccess(authSession);
        }
      }
    } catch (err) {
      setLoading(false);
      const error = err;
      handleApiRequestError(error, resetForm, () => signInRequest(formValues, resetForm, true));
    }
  }, [osType, osVersion, app, appVersion, deviceId, installationId, appHash, navigateToEmailVerification, hasPendingLibreConsents, navigateToPhoneVerification, handleApiRequestError]);
  const onSubmit = useCallback(async (values, {
    setSubmitting,
    resetForm
  }) => {
    signInRequest(values, resetForm).finally(() => {
      setSubmitting(false);
    });
  }, [signInRequest]);
  const handleClickForgotPass = useCallback(() => {
    mediator.publish('router:navigate', '/forgot-password');
  }, []);
  const handleClickCreateAccount = useCallback(() => {
    /*
      Need to use the redirect method to replaces the current location in browser history.
      When the user clicks the back button on the create account page, it should redirect the user back to the app.
    */
    mediator.publish('router:redirect', '/create-account');
  }, []);
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    errors,
    touched,
    values
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnBlur: true,
    initialErrors: {
      email: i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail'),
      password: i18n.t('Login.content.signInForm.formField.email.errors.required')
    }
  });
  const appName = i18n.t('Global.appNames.libreView');
  return /*#__PURE__*/React.createElement(MainContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 403,
      columnNumber: 5
    }
  }, loading && /*#__PURE__*/React.createElement(LoadingModal, {
    "aria-label": "Loading",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 404,
      columnNumber: 19
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen, {
    topNavigationProps: {
      animatedTitle: true,
      title: i18n.t('Login.title'),
      buttonStart: {
        element: /*#__PURE__*/React.createElement(TopHeaderBackArrow, {
          testID: "Auth.action-go-back",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 410,
            columnNumber: 22
          }
        }),
        label: 'Go back',
        // TODO: temporary, no "back button" in translations found
        onPress: () => notifyApp('adc-webview:navigate-back-app')
      }
    },
    testID: "Auth",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 405,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 417,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 418,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Text, {
    testID: "Auth.useFreestyleAccount",
    nativeID: "Auth.useFreestyleAccount",
    marginTop: -8,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 419,
      columnNumber: 13
    }
  }, i18n.t('Login.subtitle', {
    appName
  })), lockoutMessage ? /*#__PURE__*/React.createElement(Banner, {
    testID: "Login",
    message: lockoutMessage,
    submessage: lockoutSubmessage,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 427,
      columnNumber: 15
    }
  }) : null, /*#__PURE__*/React.createElement(FormContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 429,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('Login.content.signInForm.formField.email.label'),
    placeholder: i18n.t('Login.content.signInForm.formField.email.placeholder'),
    label: i18n.t('Login.content.signInForm.formField.email.label'),
    errorMessage: errors.email,
    isInvalid: errors.email && touched.email ? true : false,
    value: values.email,
    testID: "email",
    onChangeText: handleChange('email'),
    onBlur: handleBlur('email'),
    inputMode: "email",
    disabled: isLocked,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 430,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(InputPassword, {
    placeholder: i18n.t('Login.content.signInForm.formField.password.placeholder'),
    label: i18n.t('Login.content.signInForm.formField.password.label'),
    isInvalid: errors.password && touched.password ? true : false,
    errorMessage: errors.password,
    testID: "password",
    onBlur: handleBlur('password'),
    onChangeText: handleChange('password'),
    disabled: isLocked,
    value: values.password,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 443,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Stack, {
    flexDirection: "column",
    alignItems: "center",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 456,
      columnNumber: 15
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "tertiary",
    label: i18n.t('Login.content.signInForm.secondaryText'),
    onPress: handleClickForgotPass,
    testID: "Auth.forgotPassword",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 457,
      columnNumber: 17
    }
  })))), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 466,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Footer, {
    buttonLabel: i18n.t('Login.content.footerCreateAccount.links.signIn.label'),
    onButtonPress: handleSubmit,
    buttonTestID: "Login.signIn-btn",
    secondarySectionLabel: i18n.t('Login.content.footerCreateAccount.primaryText'),
    onSecondaryButtonPress: handleClickCreateAccount,
    secondaryButtonTestID: "Login.createAccount-btn",
    secondaryButtonLabel: i18n.t('Login.content.footerCreateAccount.links.createAccount.label'),
    disabled: isLocked || !isValid,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 467,
      columnNumber: 13
    }
  })))));
};
export default connector(Login);